import React, { useState } from 'react';
import { Box, Card, Typography, TextField, Button, IconButton, InputAdornment } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import ApiService from '../../../services/api';

import logger from '../../../utils/logger';
const PageContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f4f6f8',
  minHeight: '100vh',
  padding: '24px'
}));

const FormCard = styled(Card)(({ theme }) => ({
  borderRadius: 8,
  padding: '24px',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  color: '#0b91d6',
  marginBottom: 16,
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 600,
  color: '#6b7280',
  marginBottom: 6,
}));

const ChangePassword = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const toggle = (key) => setShow(prev => ({ ...prev, [key]: !prev[key] }));

  const validate = () => {
    setError('');
    if (!form.current_password || !form.new_password || !form.confirm_password) {
      setError('Please fill in all fields');
      return false;
    }
    if (form.new_password.length < 8) {
      setError('New password must be at least 8 characters');
      return false;
    }
    if (form.new_password !== form.confirm_password) {
      setError('New password and confirmation do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSuccess('');
    try {
      const payload = {
        oldPassword: form.current_password,
        newPassword: form.new_password,
        confirmPassword: form.confirm_password,
      };
      const res = await ApiService.post('/admin/auth/change-password', payload);
      const msg = res?.data?.message || res?.message || 'Password changed successfully';
      setSuccess(msg);
      setError('');
      setForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      const raw = err?.response?.data || err?.body || err;
      const msg = typeof raw === 'string' ? raw : (raw?.message || raw?.error || 'Failed to change password');
      setError(msg);
      logger.error('Change password error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton size="small" onClick={() => navigate(-1)}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" sx={{ ml: 1, color: '#374151' }}>Back</Typography>
      </Box>

      <FormCard>
        <SectionTitle>Change Password</SectionTitle>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 2 }}>
            <Label>Current Password</Label>
            <TextField
              fullWidth
              size="small"
              type={show.current ? 'text' : 'password'}
              name="current_password"
              value={form.current_password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => toggle('current')} edge="end">
                      {show.current ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Label>New Password</Label>
            <TextField
              fullWidth
              size="small"
              type={show.next ? 'text' : 'password'}
              name="new_password"
              value={form.new_password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => toggle('next')} edge="end">
                      {show.next ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Label>Confirm New Password</Label>
            <TextField
              fullWidth
              size="small"
              type={show.confirm ? 'text' : 'password'}
              name="confirm_password"
              value={form.confirm_password}
              onChange={handleChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => toggle('confirm')} edge="end">
                      {show.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          {error && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>{error}</Typography>
          )}
          {success && (
            <Typography variant="body2" color="success.main" sx={{ mb: 2 }}>{success}</Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={submitting} sx={{ textTransform: 'none', bgcolor: '#0b91d6', '&:hover': { bgcolor: '#0a7db7' } }}>
              {submitting ? 'Changing...' : 'Change Password'}
            </Button>
          </Box>
        </form>
      </FormCard>
    </PageContainer>
  );
};

export default ChangePassword;
